import type { BadgeTono, DesplegableTono, OpcionCombobox, OpcionSelect } from "@/components";
import type { Categoria, Composicion, ComposicionPropuesta, Cotizacion, CotizacionDetalle, DuplicadoEscaneo, EscaneoCotizacion, EstadoCotizacion, ItemVinculable, LineaCotizacion, LineaLeida, Moneda, Proveedor } from "./api";
import { sinTildes } from "./buscar";
import { aCentesimas, avisoPorcentajes, deCentesimas, formatPorcentajeCuota, porcentajeAInput, type PayloadCuota } from "./formaDePago";
import { formatFecha, formatMoneda, hoyISO } from "./format";
import { aCentavos, deCentavos, montoAInput, parseMonto } from "./gastos";
import { aplicarOrden, aPayloadItem, cantidadAInput, CLAVE_PREVIOS, validarItem, type FormItem } from "./presupuesto";
import { SIN_VALOR } from "./proveedores";

export const TODOS = "todos";

export const ETIQUETA_ESTADO: Record<EstadoCotizacion, string> = { pedido: "Pedido", recibido: "Recibido", aceptado: "Aceptado", descartado: "No elegido" };
export const TONO_ESTADO: Record<EstadoCotizacion, BadgeTono> = { pedido: "alerta", recibido: "primario", aceptado: "exito", descartado: "neutro" };
/** `descartado` se ve como "No elegido": lo pone el backend al aceptar otro de la misma subcategoría (Mati, 2026-10-16), no se
 * elige a mano. */
const ESTADOS_ELEGIBLES: EstadoCotizacion[] = ["pedido", "recibido", "aceptado"];
const opcionEstado = (e: EstadoCotizacion): OpcionSelect => ({ valor: e, etiqueta: ETIQUETA_ESTADO[e] });
export const OPCIONES_FILTRO_ESTADO: OpcionSelect[] = [{ valor: TODOS, etiqueta: "Todos los estados" }, ...[...ESTADOS_ELEGIBLES, "descartado" as const].map(opcionEstado)];

/** Estados del form: pedido o recibido y, si ya era aceptado o no elegido, también ese (para no perderlo al editar).
 * Aceptar no está acá: se hace desde la ficha, con el resumen de lo que implica (Mati, 2026-10-29). */
const ESTADOS_DEL_FORM: EstadoCotizacion[] = ["pedido", "recibido"];
export function opcionesEstadoForm(actual: EstadoCotizacion): OpcionSelect[] {
  return [...ESTADOS_DEL_FORM, ...(ESTADOS_DEL_FORM.includes(actual) ? [] : [actual])].map(opcionEstado);
}

/** Proveedores para el presupuesto: toda la agenda, los archivados con "Archivado" en la segunda línea. */
export function opcionesProveedorCotizacion(lista: Proveedor[]): OpcionCombobox[] {
  return lista.map((p) => ({ valor: String(p.id), etiqueta: p.nombre, detalle: p.activo ? null : "Archivado" }));
}

// ------------------------------------------------------------------ categoría y subcategoría (Mati, 2026-10-30)

/** "1.1 Estudio de suelos"; los de antes sin ubicar, su etiqueta (o "Sin subcategoría"). */
export function textoSubcategoria(c: Pick<Cotizacion, "numero" | "item" | "etiqueta">): string {
  if (c.numero !== null && c.item !== null) return `${c.numero} ${c.item}`;
  return c.etiqueta ?? "Sin subcategoría";
}

/** Para el `Select` "Subcategoría": "1.1 Estudio de suelos", que se encuentra también escribiendo la categoría. */
export function opcionesSubcategoria(categorias: Categoria[]): OpcionSelect[] {
  return categorias.flatMap((c) => c.subcategorias.map((s) => ({ valor: String(s.id), etiqueta: `${s.numero} ${s.descripcion}`, busqueda: c.nombre })));
}

/** Cómo va una rama (Mati, 2026-10-30): `futura` sin presupuestos todavía, `tramite` con presupuestos y ninguno aceptado
 * (se ve en amarillo y abierta), `cerrada` con uno aceptado (en verde y plegada). */
export type EtapaRama = "futura" | "tramite" | "cerrada";

export const TONO_ETAPA: Record<EtapaRama, DesplegableTono> = { futura: "neutro", tramite: "alerta", cerrada: "exito" };

/** La categoría: futura mientras no se empezó ninguna subcategoría, cerrada cuando están todas cerradas y en trámite
 * mientras le falte cerrar alguna (Mati, 2026-10-30: "que siga amarilla mientras le falten subcategorías por cerrar"). */
function etapaCategoria(subcategorias: NodoSubcategoria[]): EtapaRama {
  if (subcategorias.every((s) => s.etapa === "futura")) return "futura";
  return subcategorias.every((s) => s.etapa === "cerrada") ? "cerrada" : "tramite";
}

export interface NodoSubcategoria {
  id: number;
  numero: string;
  descripcion: string;
  cotizaciones: Cotizacion[];
  /** El aceptado (hay uno solo por subcategoría), o null. */
  aceptada: Cotizacion | null;
  etapa: EtapaRama;
}

export interface NodoCategoria {
  id: number;
  numero: string;
  nombre: string;
  activo: boolean;
  subcategorias: NodoSubcategoria[];
  /** Presupuestos en sus subcategorías y cuántos aceptados. */
  cantidad: number;
  aceptadas: number;
  etapa: EtapaRama;
}

export interface ArbolCotizaciones {
  categorias: NodoCategoria[];
  /** Los de antes de las subcategorías (o de una que ya no está): para ubicarlos. */
  sinSubcategoria: Cotizacion[];
}

/** El orden que el admin acaba de mover (se ve antes de que vuelva del servidor): null / sin clave = el del servidor. */
export interface OrdenArbol {
  categorias: number[] | null;
  subcategorias: Record<number, number[]>;
}

export const SIN_ORDEN: OrdenArbol = { categorias: null, subcategorias: {} };

/** El árbol de Plata → Presupuestos: categoría → subcategoría → presupuestos (en el orden en que llegan). El número
 * sale de la posición, como en Obra → Presupuesto, así una movida se ve numerada al toque; si la primera es Trámites
 * previos, va desde 0. `soloConPresupuestos` (con filtros): sólo las ramas con alguno. `empezadas`: subcategorías con
 * algún paso del manual hecho, que están en trámite aunque todavía no tengan presupuestos. */
export function armarArbol(
  categorias: Categoria[], cotizaciones: Cotizacion[], orden: OrdenArbol = SIN_ORDEN, soloConPresupuestos = false, empezadas: ReadonlySet<number> = new Set(),
): ArbolCotizaciones {
  const porItem = new Map<number, Cotizacion[]>();
  for (const c of cotizaciones) if (c.item_id !== null) porItem.set(c.item_id, [...(porItem.get(c.item_id) ?? []), c]);
  const ubicadas = new Set<number>();
  const enOrden = aplicarOrden(categorias, orden.categorias, (c) => c.id);
  const primero = enOrden[0]?.clave === CLAVE_PREVIOS ? 0 : 1;
  const nodos = enOrden.map((cat, pos): NodoCategoria => {
    const numero = String(pos + primero);
    const subcategorias = aplicarOrden(cat.subcategorias, orden.subcategorias[cat.id] ?? null, (s) => s.id).map((s, n): NodoSubcategoria => {
      const de = porItem.get(s.id) ?? [];
      ubicadas.add(s.id);
      const aceptada = de.find((c) => c.estado === "aceptado") ?? null;
      const etapa: EtapaRama = aceptada ? "cerrada" : de.length > 0 || empezadas.has(s.id) ? "tramite" : "futura";
      return { id: s.id, numero: `${numero}.${n + 1}`, descripcion: s.descripcion, cotizaciones: de, aceptada, etapa };
    });
    return {
      id: cat.id, numero, nombre: cat.nombre, activo: cat.activo, subcategorias,
      cantidad: subcategorias.reduce((t, s) => t + s.cotizaciones.length, 0),
      aceptadas: subcategorias.filter((s) => s.aceptada).length,
      etapa: etapaCategoria(subcategorias),
    };
  });
  const sinSubcategoria = cotizaciones.filter((c) => c.item_id === null || !ubicadas.has(c.item_id));
  if (!soloConPresupuestos) return { categorias: nodos, sinSubcategoria };
  return {
    categorias: nodos.filter((c) => c.cantidad > 0).map((c) => ({ ...c, subcategorias: c.subcategorias.filter((s) => s.cotizaciones.length > 0) })),
    sinSubcategoria,
  };
}

/** "Sin presupuestos", "1 presupuesto · aceptado", "3 presupuestos". */
export function textoCantidad(cantidad: number, aceptadas = 0): string {
  const base = cantidad === 0 ? "Sin presupuestos" : cantidad === 1 ? "1 presupuesto" : `${cantidad} presupuestos`;
  if (aceptadas === 0) return base;
  return `${base} · ${aceptadas === 1 ? "1 aceptado" : `${aceptadas} aceptados`}`;
}

/** Nombre de una categoría (hasta 80, como el rubro) o una subcategoría (hasta 500, como el ítem). */
export function validarNombre(texto: string, que: "categoria" | "subcategoria"): string | undefined {
  const t = texto.trim();
  const max = que === "categoria" ? 80 : 500;
  if (!t) return "Escribí el nombre";
  if (t.length > max) return `Hasta ${max} caracteres`;
  return undefined;
}

/** Vencido: tiene `valido_hasta` anterior a hoy y no se cerró (ni aceptado ni no elegido). Fechas ISO: se comparan como texto. */
export function estaVencida(c: Pick<Cotizacion, "valido_hasta" | "estado">, hoy = hoyISO()): boolean {
  return c.valido_hasta !== null && c.valido_hasta < hoy && c.estado !== "aceptado" && c.estado !== "descartado";
}

// ------------------------------------------------------------------ aceptar (desde la ficha)

/** Lo que conviene revisar antes de aceptar (Mati, 2026-10-29: "es como un aceptar sin validación"). Son avisos: se
 * puede aceptar igual. */
export function avisosAceptar(c: CotizacionDetalle, hoy = hoyISO()): string[] {
  const avisos: string[] = [];
  if (c.adjuntos.length === 0) avisos.push("No tiene adjunto el PDF o la foto del presupuesto.");
  if (c.cuotas.length === 0) avisos.push("No tiene forma de pago: el cash flow no va a saber cuándo se paga.");
  const porcentajes = avisoPorcentajes(c.cuotas.length, c.suma_porcentajes);
  if (porcentajes) avisos.push(porcentajes);
  if (c.composicion === null) avisos.push("No dice qué cubre (materiales, mano de obra o los dos).");
  if (c.valido_hasta !== null && c.valido_hasta < hoy) avisos.push(`Venció el ${formatFecha(c.valido_hasta)}: confirmá con el proveedor que mantiene el precio.`);
  return avisos;
}

/** Los de la misma subcategoría que pasan a "No elegido" al aceptar este (como hace el backend), incluido otro
 * aceptado. Uno de antes sin subcategoría: los otros sin subcategoría de su etiqueta. */
export function quedanNoElegidos(c: Pick<Cotizacion, "id" | "item_id" | "etiqueta_id">, lista: Cotizacion[]): Cotizacion[] {
  const mismo = (o: Cotizacion) => (c.item_id !== null ? o.item_id === c.item_id : o.item_id === null && o.etiqueta_id === c.etiqueta_id);
  return lista.filter((o) => o.id !== c.id && mismo(o) && o.estado !== "descartado");
}

/** Query del GET de los que compiten con este al aceptarlo (ver `quedanNoElegidos`). */
export function queryMismaSubcategoria(c: Pick<Cotizacion, "item_id" | "etiqueta_id">): string {
  return c.item_id !== null ? `?item_id=${c.item_id}` : `?etiqueta_id=${c.etiqueta_id ?? 0}`;
}

// ------------------------------------------------------------------ filtros

export interface FiltrosCotizaciones {
  estado: string;
  /** Id del proveedor que llega por `?proveedor=<id>` (desde la ficha), o null. */
  proveedorId: string | null;
}

/** Query del GET: el filtro de texto se aplica en el cliente (`buscarCotizaciones`). */
export function queryCotizaciones(f: FiltrosCotizaciones): string {
  const p = new URLSearchParams();
  if (f.proveedorId) p.set("proveedor_id", f.proveedorId);
  if (f.estado !== TODOS) p.set("estado", f.estado);
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** Busca por proveedor o descripción, sin mayúsculas ni tildes. */
export function buscarCotizaciones(lista: Cotizacion[], texto: string): Cotizacion[] {
  const t = sinTildes(texto.trim());
  if (!t) return lista;
  return lista.filter((c) => sinTildes(c.proveedor).includes(t) || sinTildes(c.descripcion ?? "").includes(t));
}

// ------------------------------------------------------------------ comparación

export interface FilaComparacion {
  cotizacion: Cotizacion;
  masBarato: boolean;
  /** Diferencia contra el más barato, en USD ("0.00" para el más barato y los empatados). */
  diferenciaUsd: string;
  /** Diferencia en %, con 1 decimal ("12.5"); null si el más barato vale USD 0 (no hay contra qué dividir). */
  diferenciaPorcentaje: string | null;
  vencida: boolean;
  aceptada: boolean;
  /** Puntaje de 0 a 100 con 1 decimal ("80.0"): precio contra el más barato + Validado + De parte de (`PESOS_PUNTAJE`). */
  puntaje: string;
  /** El de puntaje más alto (con empate, todos los del máximo). */
  masConveniente: boolean;
}

/** Pesos del puntaje (Mati, 2026-10-19): fijos, suman 100. El precio se puntúa contra el más barato (más barato ÷ precio). */
export const PESOS_PUNTAJE = { precio: 60n, validado: 30n, recomendado: 10n } as const;

/** Puntaje en décimas: PESO_PRECIO × mínimo ÷ precio (mitad para arriba) + los de confianza. Con precio 0 → todo el peso del precio. */
function decimasDePuntaje(c: Cotizacion, centavos: bigint, minimo: bigint): bigint {
  const escalado = minimo * PESOS_PUNTAJE.precio * 10n;
  let precio = centavos === 0n ? PESOS_PUNTAJE.precio * 10n : escalado / centavos;
  if (centavos !== 0n && (escalado % centavos) * 2n >= centavos) precio += 1n;
  const validado = c.proveedor_validado ? PESOS_PUNTAJE.validado * 10n : 0n;
  const recomendado = c.proveedor_recomendado_por?.trim() ? PESOS_PUNTAJE.recomendado * 10n : 0n;
  return precio + validado + recomendado;
}

/** Redondeo a 1 decimal, mitad hacia arriba (los valores son ≥ 0). `num/den` en décimas. */
function decimasDePorcentaje(diferencia: bigint, base: bigint): string {
  const escalado = diferencia * 1000n; // ×100 (%) ×10 (una décima)
  let decimas = escalado / base;
  if ((escalado % base) * 2n >= base) decimas += 1n;
  return `${decimas / 10n}.${decimas % 10n}`;
}

/**
 * Presupuestos para comparar: todos los que tienen monto (también los no elegidos), del puntaje más alto al más bajo; a igual puntaje,
 * por `monto_usd` ascendente (empate: el más viejo primero, luego por id). Todos los que empatan con el mínimo quedan como "más barato" y
 * con el puntaje máximo, como "más conveniente". Cuentas en centavos (`BigInt`).
 */
export function compararCotizaciones(lista: Cotizacion[], hoy = hoyISO()): FilaComparacion[] {
  const conMonto = lista
    .filter((c) => c.monto_usd !== null)
    .map((c) => ({ c, centavos: aCentavos(c.monto_usd as string) }))
    .sort((a, b) => (a.centavos === b.centavos ? (a.c.fecha === b.c.fecha ? a.c.id - b.c.id : a.c.fecha < b.c.fecha ? -1 : 1) : a.centavos < b.centavos ? -1 : 1));
  const primero = conMonto[0];
  if (!primero) return [];
  const minimo = primero.centavos;
  const conPuntaje = conMonto.map(({ c, centavos }) => ({ c, centavos, decimas: decimasDePuntaje(c, centavos, minimo) }));
  const maximo = conPuntaje.reduce((m, x) => (x.decimas > m ? x.decimas : m), 0n);
  // Estable: a igual puntaje queda el orden por precio (y fecha, id) de arriba.
  return [...conPuntaje]
    .sort((a, b) => (a.decimas === b.decimas ? 0 : a.decimas > b.decimas ? -1 : 1))
    .map(({ c, centavos, decimas }) => {
      const diferencia = centavos - minimo;
      return {
        cotizacion: c,
        masBarato: diferencia === 0n,
        diferenciaUsd: deCentavos(diferencia),
        diferenciaPorcentaje: minimo === 0n ? null : decimasDePorcentaje(diferencia, minimo),
        vencida: estaVencida(c, hoy),
        aceptada: c.estado === "aceptado",
        puntaje: `${decimas / 10n}.${decimas % 10n}`,
        masConveniente: decimas === maximo,
      };
    });
}

/** "80.0" → "80"; "8.6" → "8,6". */
export function formatPuntaje(puntaje: string): string {
  return puntaje.replace(/\.0$/, "").replace(".", ",");
}

/** "12.5" → "+12,5 %"; "0.0" → "0 %". */
export function formatDiferenciaPorcentaje(pct: string | null): string {
  if (pct === null) return "—";
  if (/^0\.0$/.test(pct)) return "0 %";
  return `+${pct.replace(/\.0$/, "").replace(".", ",")} %`;
}

// ------------------------------------------------------------------ composición (materiales / mano de obra)

/** Valor del `Select` para un presupuesto sin marcar. */
export const SIN_COMPOSICION = "sin-marcar";

export const ETIQUETA_COMPOSICION: Record<Composicion, string> = { materiales: "Materiales", mano_obra: "Mano de obra", mixto: "Materiales y mano de obra" };

export const OPCIONES_COMPOSICION: OpcionSelect[] = [
  { valor: SIN_COMPOSICION, etiqueta: "Sin marcar" },
  ...(Object.keys(ETIQUETA_COMPOSICION) as Composicion[]).map((c) => ({ valor: c, etiqueta: ETIQUETA_COMPOSICION[c] })),
];

/** "Materiales y mano de obra (60 % materiales)", "Mano de obra" o "Sin marcar". */
export function textoComposicion(c: { composicion: Composicion | null; porcentaje_materiales: string | null }): string {
  if (c.composicion === null) return "Sin marcar";
  const nombre = ETIQUETA_COMPOSICION[c.composicion];
  return c.composicion === "mixto" && c.porcentaje_materiales !== null ? `${nombre} (${formatPorcentajeCuota(c.porcentaje_materiales)} materiales)` : nombre;
}

/** La propuesta según los ítems, si es distinta de lo marcado: "Según los ítems que cubre: …". */
export function textoPropuesta(d: Pick<CotizacionDetalle, "composicion" | "porcentaje_materiales" | "composicion_propuesta">): string | null {
  const p: ComposicionPropuesta | null = d.composicion_propuesta;
  if (!p || (p.composicion === d.composicion && p.porcentaje_materiales === d.porcentaje_materiales)) return null;
  return `Según los ítems que cubre: ${textoComposicion(p)}.`;
}

function errorPorcentajeMateriales(texto: string): string | undefined {
  if (!texto.trim()) return undefined; // vacío: el backend toma el de los ítems
  const n = aCentesimas(texto);
  if (n === null) return "Cargá un % válido (hasta 2 decimales)";
  if (n <= 0 || n >= 10000) return "Entre 0 y 100: si es todo materiales o todo mano de obra, elegí esa opción";
  return undefined;
}

// ------------------------------------------------------------------ formulario

export interface FormCotizacion {
  proveedor_id: string;
  /** Id de la subcategoría, o "" sin elegir (los de antes, hasta ubicarlos). */
  item_id: string;
  descripcion: string;
  fecha: string;
  estado: EstadoCotizacion;
  monto: string;
  moneda: Moneda;
  valido_hasta: string;
  notas: string;
  /** `SIN_COMPOSICION` o una `Composicion`. */
  composicion: string;
  /** Sólo en mixto; vacío = el de los ítems que cubre. */
  porcentaje_materiales: string;
}

export type ErroresCotizacion = Partial<
  Record<"proveedor_id" | "item_id" | "descripcion" | "fecha" | "monto" | "valido_hasta" | "notas" | "porcentaje_materiales", string>
>;

export interface PayloadCotizacion {
  proveedor_id: number;
  item_id: number;
  descripcion: string | null;
  fecha: string;
  estado: EstadoCotizacion;
  monto: string | null;
  moneda: Moneda | null;
  valido_hasta: string | null;
  notas: string | null;
  composicion: Composicion | null;
  porcentaje_materiales: string | null;
  /** Sólo en el alta: p. ej. las líneas que leyó el escaneo. */
  lineas?: PayloadLinea[];
  /** Sólo en el alta: la forma de pago que leyó el escaneo. */
  cuotas?: PayloadCuota[];
}

/** Sin tipo de cambio: el backend toma el del presupuesto de obra (Mati, 2026-10-29: "el TC va cuando hago el pago"). */
export function formCotizacion(c?: Cotizacion, hoy = hoyISO()): FormCotizacion {
  if (!c) {
    return { proveedor_id: "", item_id: "", descripcion: "", fecha: hoy, estado: "recibido", monto: "", moneda: "ARS", valido_hasta: "", notas: "",
      composicion: SIN_COMPOSICION, porcentaje_materiales: "",
    };
  }
  return {
    proveedor_id: String(c.proveedor_id), item_id: c.item_id === null ? "" : String(c.item_id), descripcion: c.descripcion ?? "", fecha: c.fecha, estado: c.estado,
    monto: c.monto ? montoAInput(c.monto) : "", moneda: c.moneda ?? "ARS",
    valido_hasta: c.valido_hasta ?? "", notas: c.notas ?? "", composicion: c.composicion ?? SIN_COMPOSICION,
    porcentaje_materiales: c.porcentaje_materiales ? porcentajeAInput(c.porcentaje_materiales) : "",
  };
}


/** Qué decir cuando lo escaneado parece un presupuesto ya cargado (Mati, 2026-10-29: escaneó dos veces el mismo y
 * quedó duplicado). Mismo archivo: ya está, no se ofrece cargarlo. Mismos datos: puede ser otro, se ofrece igual. */
export function mensajeDuplicado(d: DuplicadoEscaneo, archivo: string): { titulo: string; texto: string; cargarIgual: boolean } {
  const monto = d.monto !== null && d.moneda !== null ? ` por ${formatMoneda(d.monto, d.moneda)}` : "";
  const cual = `de ${d.proveedor} del ${formatFecha(d.fecha)}${monto} (${ETIQUETA_ESTADO[d.estado]})`;
  if (d.mismo_archivo) return { titulo: "Este presupuesto ya está cargado", texto: `«${archivo}» ya está adjunto al presupuesto ${cual}.`, cargarIgual: false };
  return {
    titulo: "Parece que ya está cargado",
    texto: `Ya hay un presupuesto ${cual}, del mismo proveedor y por el mismo monto. Si es el mismo, abrilo; si es otro, cargalo igual.`,
    cargarIgual: true,
  };
}

/** Form del alta completado con lo que leyó el escaneo; lo que no se leyó queda como en un alta nueva (fecha de hoy,
 * ARS). Queda "recibido": es un presupuesto que llegó. */
export function formDesdeEscaneo(e: EscaneoCotizacion, hoy = hoyISO()): FormCotizacion {
  const vacio = formCotizacion(undefined, hoy);
  return {
    ...vacio,
    proveedor_id: e.proveedor_id === null ? "" : String(e.proveedor_id),
    item_id: e.item_id === null ? "" : String(e.item_id),
    descripcion: e.descripcion ?? "",
    fecha: e.fecha ?? hoy,
    monto: e.monto ? montoAInput(e.monto) : "",
    moneda: e.moneda ?? vacio.moneda,
    valido_hasta: e.valido_hasta && e.valido_hasta >= (e.fecha ?? hoy) ? e.valido_hasta : "",
    notas: e.notas ?? "",
    composicion: e.composicion ?? SIN_COMPOSICION,
    porcentaje_materiales: e.porcentaje_materiales ? porcentajeAInput(e.porcentaje_materiales) : "",
  };
}

/** Mismas reglas que el backend: "pedido" admite no tener monto; los demás estados exigen monto > 0. */
export function validarCotizacion(f: FormCotizacion, proveedorAAgregar = false): ErroresCotizacion {
  const e: ErroresCotizacion = {};
  if ((!f.proveedor_id || f.proveedor_id === SIN_VALOR) && !proveedorAAgregar) e.proveedor_id = "Elegí un proveedor";
  if (!/^\d+$/.test(f.item_id)) e.item_id = "Elegí una subcategoría";
  if (f.descripcion.trim().length > 200) e.descripcion = "Hasta 200 caracteres";
  if (f.notas.trim().length > 2000) e.notas = "Hasta 2000 caracteres";
  if (!f.fecha) e.fecha = "Ingresá la fecha";
  const hayMonto = f.monto.trim() !== "";
  if (hayMonto ? parseMonto(f.monto) === null : f.estado !== "pedido") e.monto = "Ingresá un monto mayor a cero";
  if (f.valido_hasta && f.fecha && f.valido_hasta < f.fecha) e.valido_hasta = "Tiene que ser igual o posterior a la fecha";
  const pct = f.composicion === "mixto" ? errorPorcentajeMateriales(f.porcentaje_materiales) : undefined;
  if (pct) e.porcentaje_materiales = pct;
  return e;
}

const vacioANull = (s: string) => (s.trim() === "" ? null : s.trim());

/** Cuerpo del POST/PATCH. Asume que `validarCotizacion(f)` no devolvió errores. Sin monto, la moneda va null. */
export function aPayloadCotizacion(f: FormCotizacion): PayloadCotizacion {
  const monto = f.monto.trim() ? parseMonto(f.monto) : null;
  return {
    proveedor_id: Number(f.proveedor_id),
    item_id: Number(f.item_id),
    descripcion: vacioANull(f.descripcion),
    fecha: f.fecha,
    estado: f.estado,
    monto,
    moneda: monto === null ? null : f.moneda,
    valido_hasta: f.valido_hasta || null,
    notas: vacioANull(f.notas),
    composicion: f.composicion === SIN_COMPOSICION ? null : (f.composicion as Composicion),
    porcentaje_materiales: f.composicion === "mixto" && f.porcentaje_materiales.trim() ? deCentesimas(aCentesimas(f.porcentaje_materiales) ?? 0) : null,
  };
}

export function mensajeBorrado(c: Cotizacion): string {
  const adj = c.cantidad_adjuntos;
  const extra = adj === 0 ? "" : ` Se borra${adj === 1 ? " también su adjunto" : `n también sus ${adj} adjuntos`}.`;
  return `¿Borrar el presupuesto de ${c.proveedor} (${textoSubcategoria(c)})?${extra} No se puede deshacer.`;
}

// ------------------------------------------------------------------ líneas (el desglose del presupuesto)

export const SIN_VINCULO = "sin-vincular";

/** El form de una línea: el de un ítem del rubro (el precio, en la moneda del presupuesto) más el ítem vinculado. */
export interface FormLinea extends FormItem {
  item: string;
}

export interface PayloadLinea {
  descripcion: string;
  unidad: string | null;
  cantidad: string | null;
  precio_unitario: string | null;
  presupuesto_item_id: number | null;
}

export function formLinea(l?: LineaCotizacion): FormLinea {
  if (!l) return { descripcion: "", unidad: "", cantidad: "", precio: "", item: SIN_VINCULO };
  return {
    descripcion: l.descripcion,
    unidad: l.unidad ?? "",
    cantidad: l.cantidad === null ? "" : cantidadAInput(l.cantidad),
    precio: l.precio_unitario === null ? "" : montoAInput(l.precio_unitario),
    item: l.presupuesto_item_id === null ? SIN_VINCULO : String(l.presupuesto_item_id),
  };
}

export const validarLinea = (f: FormLinea) => validarItem(f);

export function aPayloadLinea(f: FormLinea): PayloadLinea {
  return { ...aPayloadItem(f), presupuesto_item_id: f.item === SIN_VINCULO ? null : Number(f.item) };
}

/** Las líneas leídas por el escaneo, como van en el alta (sin vincular). */
export function lineasDesdeEscaneo(lineas: LineaLeida[]): PayloadLinea[] {
  return lineas.map((l) => ({ ...l, presupuesto_item_id: null }));
}

/** "Albañilería › Mampostería 0,15": el ítem con su rubro. */
export function nombreItemVinculable(i: ItemVinculable): string {
  return `${i.rubro} › ${i.descripcion}`;
}

export function opcionesItemVinculable(items: ItemVinculable[]): OpcionSelect[] {
  return [{ valor: SIN_VINCULO, etiqueta: "Sin vincular" }, ...items.map((i) => ({ valor: String(i.id), etiqueta: nombreItemVinculable(i) }))];
}

/** Aviso si la suma de las líneas no da el total del presupuesto (lo normal si suma IVA, descuentos o ajustes). */
export function avisoDiferenciaLineas(d: Pick<CotizacionDetalle, "suma_lineas" | "diferencia" | "monto" | "moneda">): string | null {
  if (d.suma_lineas === null || d.moneda === null || d.monto === null || d.diferencia === null) return null;
  if (aCentavos(d.diferencia.replace("-", "")) === 0n) return null;
  const negativa = d.diferencia.startsWith("-");
  const diferencia = formatMoneda(negativa ? d.diferencia.slice(1) : d.diferencia, d.moneda);
  return `Las líneas suman ${formatMoneda(d.suma_lineas, d.moneda)} y el total del presupuesto es ${formatMoneda(d.monto, d.moneda)}: diferencia de ${negativa ? "−" : "+"}${diferencia} (IVA, descuentos o ajustes).`;
}
